import { makeStyles, tokens } from '@fluentui/react-components'

const actionSlotVisible = {
  width: tokens.spacingHorizontalXXXL,
  flexShrink: 0,
} as const

const useStyles = makeStyles({
  card: {
    width: '100%',
    height: 'fit-content',
    padding: tokens.spacingHorizontalNone,
  },
  cardDisabled: {
    opacity: 0.5,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardWorkingDocument: {
    borderRadius: tokens.borderRadius3XLarge,
  },
  treeItemLayout: {
    height: 'fit-content',
    minHeight: tokens.spacingVerticalXXXL,
    ['& .fui-TreeItemLayout__main']: {
      display: 'flex',
      flexDirection: 'row',
      flex: 1,
      paddingLeft: tokens.spacingHorizontalNone,
      minWidth: 0,
      overflow: 'hidden',
      gap: tokens.spacingHorizontalXXS,
      alignItems: 'center',
    },
  },
  cardHeader: {
    flex: 1,
    minWidth: 0,
    padding: `${tokens.spacingVerticalSNudge} 0 ${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalS}`,
    ['& .fui-CardHeader__header']: {
      overflow: 'hidden',
    },
    ['& .fui-CardHeader__image']: {
      width: tokens.spacingHorizontalXL,
      height: tokens.spacingHorizontalXL,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    ['& .fui-CardHeader__action']: {
      width: 0,
      overflow: 'hidden',
      padding: 0,
      margin: 0,
    },
  },
  moreActionsButton: {
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  moreActionsButtonVisible: {
    opacity: 1,
  },
  sectionActionSlot: {
    width: 0,
    overflow: 'hidden',
    flexShrink: 0,
  },
  sectionActionSlotVisible: {
    ...actionSlotVisible,
  },
  cardHeaderActionVisible: {
    ['& .fui-CardHeader__action']: {
      ...actionSlotVisible,
    },
  },
  fileRow: {
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    ['&:hover']: {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ['&:active']: {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ['&:hover .fui-Button, &:has(:focus-visible) .fui-Button']: {
      opacity: 1,
    },
    ['&:hover .fui-CardHeader__action, &:has(:focus-visible) .fui-CardHeader__action']: {
      ...actionSlotVisible,
    },
    ['& .fui-TreeItemLayout:hover']: {
      backgroundColor: 'transparent',
    },
    ['& .fui-TreeItemLayout:active']: {
      backgroundColor: 'transparent',
    },
  },
  fileRowWorkingDocument: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  treeContainer: {
    paddingBottom: tokens.spacingVerticalNone,
    rowGap: tokens.spacingVerticalNone,
    ['& .fui-Checkbox__indicator']: {
      transitionProperty: 'background-color, border-color, color',
      transitionDuration: tokens.durationNormal,
      transitionTimingFunction: tokens.curveEasyEase,
    },
  },
  treeChildBase: {
    padding: `0 ${tokens.spacingHorizontalS}`,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ['&:hover']: {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ['&:active']: {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ['&:hover .fui-TreeItemLayout']: {
      backgroundColor: 'transparent',
    },
    ['& .fui-TreeItemLayout:active']: {
      backgroundColor: 'transparent',
    },
    ['&:hover .fui-Button, &:has(:focus-visible) .fui-Button']: {
      opacity: 1,
    },
    ['&:hover .section-action-slot, &:has(:focus-visible) .section-action-slot']: {
      ...actionSlotVisible,
    },
  },
  treeChildLastWorkingDocument: {
    marginBottom: tokens.spacingVerticalS,
  },
  treeItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusNone,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ['&:hover']: {
      backgroundColor: tokens.colorBrandBackground2,
    },
    ['&:active']: {
      backgroundColor: tokens.colorBrandBackground2Pressed,
    },
    ['& .fui-TreeItemLayout']: {
      backgroundColor: 'transparent',
    },
    ['& .fui-TreeItemLayout:hover']: {
      backgroundColor: 'transparent',
    },
    ['& .fui-TreeItemLayout:active']: {
      backgroundColor: 'transparent',
    },
  },
  menuItem: {
    ['& .fui-MenuItem__secondaryContent']: {
      height: tokens.spacingHorizontalL,
    },
  },
  menuItemSecondaryIcon: {
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
  },
  stateMessage: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    gap: tokens.spacingVerticalXXS,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  title: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  titleSelected: {
    fontWeight: tokens.fontWeightSemibold,
  },
  fileRowExpanded: {
    borderBottomWidth: tokens.strokeWidthThin,
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  braveTitle: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  noIconOverride: {
    ['& .fui-TreeItemLayout__main']: {
      paddingLeft: 0,
      justifyContent: 'space-between',
    },
  },
  noLeftPadding: {
    paddingLeft: 0,
  },
  sectionText: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  excludedIcon: {
    color: tokens.colorNeutralForeground4,
    flexShrink: 0,
    fontSize: tokens.fontSizeBase500,
  },
  loadingSpinner: {
    padding: tokens.spacingHorizontalSNudge,
  },
})

export { useStyles }
